import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate } from '../utils/pdfHelpers';
import {
  PdfPageHeader,
  PdfPatientBox,
  PdfCallReport,
  PdfAccentSectionHeader,
  PdfLabelValue,
  PdfCommentsBox,
  PdfFooter
} from '../utils/PdfSharedComponents';
import { getLungScreeningEligibility, SDM_CHECKLIST_ITEMS } from '../utils/lungScreeningEligibility';

export function LungScreeningPDF({ data, facilityInfo }) {
  const reason = data.reason && String(data.reason).trim() ? String(data.reason).trim() : 'Lung cancer screening';
  const eligibility = getLungScreeningEligibility(data);
  const sdm = data.sdmVisit || {};
  const symptoms = data.symptoms || [];

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="LUNG CANCER SCREENING WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
          technologistLabel="Nurse"
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfAccentSectionHeader title="ELIGIBILITY DETERMINATION" />
            <View
              style={{
                border: `2px solid ${eligibility.eligible ? COLORS.normal : COLORS.abnormal}`,
                borderRadius: 5,
                backgroundColor: eligibility.eligible ? COLORS.lightGray : '#fdecea',
                padding: 8,
                marginBottom: 8,
              }}
            >
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: 'Helvetica-Bold',
                  color: eligibility.eligible ? COLORS.normal : COLORS.abnormal,
                  marginBottom: 4,
                }}
              >
                {eligibility.eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
              </Text>
              {!eligibility.eligible && eligibility.reasons.map((r, i) => (
                <Text key={i} style={[commonStyles.infoBoxText, { color: COLORS.abnormal }]}>• {r}</Text>
              ))}
              <Text style={[commonStyles.infoBoxText, { marginTop: 4, fontSize: 8, color: '#888' }]}>
                Per CMS NCD 210.14: age 50-77, asymptomatic, {'>='}20 pack-year history,
                currently smoking or quit within 15 years. Determination aid only — not a
                substitute for clinical/billing judgment.
              </Text>
            </View>

            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="RISK FACTORS" />
            <PdfLabelValue label="Date of birth:" value={formatExamDate(data.dateOfBirth)} forceBlack />
            <PdfLabelValue label="Age at exam:" value={eligibility.age !== null ? String(eligibility.age) : ''} forceBlack />
            <PdfLabelValue label="Smoking status:" value={data.smokingStatus || ''} isAbnormal={data.smokingStatus === 'Never smoked'} />
            <PdfLabelValue label="Pack-years:" value={data.packYears ? String(data.packYears) : ''} isAbnormal={data.packYears && parseFloat(data.packYears) < 20} />
            {data.smokingStatus === 'Former smoker' && (
              <PdfLabelValue label="Years since quitting:" value={data.yearsSinceQuit ? String(data.yearsSinceQuit) : ''} isAbnormal={data.yearsSinceQuit && parseFloat(data.yearsSinceQuit) > 15} />
            )}
            <PdfLabelValue label="Symptoms:" value={symptoms.length > 0 ? symptoms.join(', ') : 'None reported'} isAbnormal={symptoms.length > 0} />

            <PdfAccentSectionHeader title="SHARED DECISION-MAKING VISIT" />
            <PdfLabelValue label="Visit date:" value={formatExamDate(sdm.date)} forceBlack />
            {SDM_CHECKLIST_ITEMS.map(item => (
              <Text key={item.key} style={[commonStyles.infoBoxText, { color: sdm[item.key] ? COLORS.normal : COLORS.abnormal }]}>
                {sdm[item.key] ? '✓' : '✗'} {item.label}
              </Text>
            ))}

            <PdfAccentSectionHeader title="SCREENING DETAILS" />
            <PdfLabelValue label="Screening type:" value={data.screeningType || ''} forceBlack />
            <PdfLabelValue label="Ordering provider:" value={data.orderingProvider || ''} forceBlack />
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
